import React, { useCallback, useEffect, useState } from "react";
import { labelBatchApi, brandingApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Modal, Button, Field, Badge } from "../../Components/UI/ui";
import { GATE, STICKER_DEFAULTS, STICKER_SIZES, STICKER_LIMITS, STICKER_MAX, openPdf, stickerCode, labName } from "../../lib/lab";

// Print blank-label stock from a roll's codes (specimens guide §2.1).
// Every change re-runs GET …/stickers/plan so the sheet maths and any refusal
// ("a 50×25 sticker does not fit…") come from the server, never guessed here.
const StickerPrintModal = ({ open, onClose, batch }) => {
  const { can } = useAuth();
  const { toast, branding, loadBranding, setBranding } = useApp();
  const admin = can(...GATE.branding);
  const [f, setF] = useState(STICKER_DEFAULTS);
  const [from, setFrom] = useState("");
  const [count, setCount] = useState(100);
  const [includeUsed, setIncludeUsed] = useState(false);
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  // Seed from the lab's saved defaults, and start at the roll's first unused code
  useEffect(() => {
    if (!open || !batch) return;
    loadBranding();
    const saved = branding?.branding?.sticker || {};
    setF({ ...STICKER_DEFAULTS, ...Object.fromEntries(Object.entries(saved).filter(([, v]) => v !== null && v !== undefined)) });
    setFrom(String(batch.rangeStart + (batch.usedCount || 0)));
    setCount(100);
    setIncludeUsed(false);
    setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batch]);

  const query = useCallback(() => {
    const o = {
      from: from === "" ? undefined : Number(from),
      count: Number(count) || undefined,
      includeUsed: includeUsed ? true : undefined,
      layout: f.layout, pageSize: f.pageSize,
      widthMm: f.widthMm, heightMm: f.heightMm, marginMm: f.marginMm, gapMm: f.gapMm,
      showLogo: f.showLogo, showName: f.showName, showText: f.showText,
      startAt: f.startAt || undefined,
      labelText: f.labelText?.trim() || undefined,
    };
    if (f.layout === "roll") { delete o.pageSize; delete o.marginMm; delete o.startAt; }
    return o;
  }, [from, count, includeUsed, f]);

  // Debounced preview — the server does the fitting and the validation
  useEffect(() => {
    if (!open || !batch) return;
    let cancelled = false;
    const h = setTimeout(async () => {
      setBusy("plan"); setErr(null);
      try {
        const p = await labelBatchApi.stickerPlan(batch._id, query());
        if (!cancelled) { setPlan(p); }
      } catch (e) {
        if (!cancelled) { setPlan(null); setErr(apiError(e)); }
      } finally { if (!cancelled) setBusy(null); }
    }, 350);
    return () => { cancelled = true; clearTimeout(h); };
  }, [open, batch, query]);

  const print = (extra) => openPdf(labelBatchApi.stickerPdfUrl(batch._id, { ...query(), ...extra }));

  const saveDefaults = async () => {
    setBusy("save");
    try {
      const sticker = {
        layout: f.layout, pageSize: f.pageSize, widthMm: Number(f.widthMm), heightMm: Number(f.heightMm),
        marginMm: Number(f.marginMm), gapMm: Number(f.gapMm),
        showLogo: !!f.showLogo, showName: !!f.showName, showText: !!f.showText,
        labelText: f.labelText?.trim() || null,
      };
      const res = await brandingApi.update({ sticker });
      setBranding(res?.branding ? res : { ...branding, branding: { ...(branding?.branding || {}), sticker } });
      toast.success("Saved as this lab's sticker defaults");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  if (!batch) return null;
  const sheet = f.layout === "sheet";
  const sizeMatch = STICKER_SIZES.find((s) => s.widthMm === Number(f.widthMm) && s.heightMm === Number(f.heightMm));
  const labTitle = labName(branding?.name);
  const lastCode = stickerCode(batch.prefix, batch.numberWidth, batch.rangeEnd);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Print stickers · ${batch.name}`}
      footer={
        <>
          {admin && <Button size="sm" variant="ghost" disabled={!!err} loading={busy === "save"} onClick={saveDefaults} style={{ marginRight: "auto" }}>Save as default</Button>}
          <Button onClick={onClose}>Close</Button>
          <Button variant="secondary" disabled={!plan?.count} onClick={() => print({ count: 3 })} title="Print 3 stickers and check them against the sheet before a full run">Test page (3)</Button>
          <Button variant="secondary" disabled={!plan?.count} onClick={() => print({ download: 1 })}>Download</Button>
          <Button variant="primary" disabled={!plan?.count} onClick={() => print()}>Print {plan?.count ? `${plan.count} stickers` : ""}</Button>
        </>
      }
    >
      <div className="lab-muted">
        Codes come from this roll ({stickerCode(batch.prefix, batch.numberWidth, batch.rangeStart)} – {lastCode}). Already-used codes are skipped, so a reprint never duplicates an identity.
      </div>

      <div className="lab-section">
        <h4>Which codes</h4>
        <div className="lab-grid cols-3">
          <Field label="Start at code" hint={from === "" ? `Roll starts at ${stickerCode(batch.prefix, batch.numberWidth, batch.rangeStart)}` : stickerCode(batch.prefix, batch.numberWidth, Number(from) || batch.rangeStart)}>
            <input type="number" className="lab-input" value={from} min={batch.rangeStart} max={batch.rangeEnd} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="How many" hint={`Up to ${STICKER_MAX} per run`}>
            <input type="number" className="lab-input" value={count} min="1" max={STICKER_MAX} onChange={(e) => setCount(e.target.value)} />
          </Field>
          <Field label="Reprint">
            <label className="lab-check" style={{ height: 40 }}>
              <input type="checkbox" checked={includeUsed} onChange={(e) => setIncludeUsed(e.target.checked)} />
              Include codes already used
            </label>
          </Field>
        </div>
      </div>

      <div className="lab-section">
        <h4>Sticker &amp; sheet</h4>
        <div className="lab-row" style={{ marginBottom: 12, gap: 8 }}>
          {STICKER_SIZES.map((s) => (
            <Button key={s.label} size="sm" variant={sizeMatch === s ? "navy" : "secondary"} onClick={() => set({ widthMm: s.widthMm, heightMm: s.heightMm })}>{s.label}</Button>
          ))}
          <span className="lab-muted" style={{ fontSize: 12 }}>or set your own below</span>
        </div>
        <div className="lab-grid cols-4">
          <Field label="Width (mm)"><input type="number" className="lab-input" value={f.widthMm} min={STICKER_LIMITS.widthMm[0]} max={STICKER_LIMITS.widthMm[1]} onChange={(e) => set({ widthMm: e.target.value })} /></Field>
          <Field label="Height (mm)"><input type="number" className="lab-input" value={f.heightMm} min={STICKER_LIMITS.heightMm[0]} max={STICKER_LIMITS.heightMm[1]} onChange={(e) => set({ heightMm: e.target.value })} /></Field>
          <Field label="Layout" hint={sheet ? "Grid on a page" : "One per page, for label printers"}>
            <select className="lab-select" value={f.layout} onChange={(e) => set({ layout: e.target.value })}>
              <option value="sheet">Sheet of labels</option>
              <option value="roll">Roll / label printer</option>
            </select>
          </Field>
          {sheet && (
            <Field label="Page size">
              <select className="lab-select" value={f.pageSize} onChange={(e) => set({ pageSize: e.target.value })}>
                <option value="A4">A4</option>
                <option value="LETTER">Letter</option>
              </select>
            </Field>
          )}
          <Field label="Gap (mm)"><input type="number" className="lab-input" value={f.gapMm} min={STICKER_LIMITS.gapMm[0]} max={STICKER_LIMITS.gapMm[1]} onChange={(e) => set({ gapMm: e.target.value })} /></Field>
          {sheet && <Field label="Page margin (mm)"><input type="number" className="lab-input" value={f.marginMm} min={STICKER_LIMITS.marginMm[0]} max={STICKER_LIMITS.marginMm[1]} onChange={(e) => set({ marginMm: e.target.value })} /></Field>}
          {sheet && (
            <Field label="Skip cells" hint="Start on a part-used sheet">
              <input type="number" className="lab-input" value={f.startAt} min="0" max={plan?.grid?.perPage ? plan.grid.perPage - 1 : 100} onChange={(e) => set({ startAt: Number(e.target.value) || 0 })} />
            </Field>
          )}
        </div>
      </div>

      <div className="lab-section">
        <h4>What's on the sticker</h4>
        <div className="lab-row" style={{ gap: 24, flexWrap: "wrap" }}>
          <label className="lab-check"><input type="checkbox" checked={!!f.showText} onChange={(e) => set({ showText: e.target.checked })} /> Code under the bars</label>
          <label className="lab-check"><input type="checkbox" checked={!!f.showName} onChange={(e) => set({ showName: e.target.checked })} /> Lab name</label>
          <label className="lab-check"><input type="checkbox" checked={!!f.showLogo} onChange={(e) => set({ showLogo: e.target.checked })} /> Logo</label>
        </div>
        {f.showName && (
          <div className="lab-grid cols-2" style={{ marginTop: 12 }}>
            <Field label="Name line" hint={`Leave blank for ${labTitle || "the lab name"}`}>
              <input className="lab-input" value={f.labelText || ""} placeholder={labTitle} onChange={(e) => set({ labelText: e.target.value })} />
            </Field>
          </div>
        )}
      </div>

      {/* The run exactly as the server resolved it */}
      <div className="lab-row between" style={{ background: "var(--ground, #f5f8f9)", borderRadius: 10, padding: "12px 16px" }}>
        {err ? <span className="lab-danger-text">{err}</span> : busy === "plan" && !plan ? <span className="lab-muted">Working out the sheet…</span> : plan ? (
          <>
            <span style={{ fontSize: 15 }}>
              {plan.options.layout === "sheet"
                ? <><b>{plan.grid.perPage} per sheet</b> ({plan.grid.columns} × {plan.grid.rows}) · <b>{plan.count} stickers</b> · {plan.pages} page{plan.pages === 1 ? "" : "s"}</>
                : <><b>{plan.count} stickers</b> · one per page</>}
              {plan.first && <span className="lab-muted"> · {plan.first} → {plan.last}</span>}
            </span>
            <span className="lab-row" style={{ gap: 6 }}>
              {plan.skippedUsed > 0 && <Badge kind="muted">{plan.skippedUsed} used, skipped</Badge>}
              {plan.truncated && <Badge kind="IN_PROCESS">capped at {plan.count}</Badge>}
            </span>
          </>
        ) : <span className="lab-muted">—</span>}
      </div>
      <div className="lab-muted" style={{ fontSize: 12 }}>
        Print the test page first and hold it against a physical sheet before a full run.
      </div>
    </Modal>
  );
};

export default StickerPrintModal;
